import React, { useRef } from "react";
import { Box, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MinusIcon from "@mui/icons-material/Remove";
import useHoverAction from "../hooks/useHoverAction";
import theme from "Theme/AppTheme";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { selectCustomStyle } from "../helpers/componentUtils";
import { compareEqConOptions, localIdOptions } from "../helpers/constants";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { LanguageIndicatorType } from "../types";
import { v4 as uuidv4 } from "uuid";
import DeleteButton from "../components/DeleteButton";
import { LocaleIds } from "models/OrganizationModels/OrganizationMetadataConfiguration";

type LanguageIndicatorDefinitionProps = LanguageIndicatorType & {
    update: (
        key: string,
        value: string | number | LocaleIds | { id: string; value: string }[],
    ) => void;
    remove: () => void;
};

const LanguageIndicatorDefinition: React.FC<LanguageIndicatorDefinitionProps> =
    ({
        update,
        remove,
        id,
        localeId,
        fieldPath,
        comparisonOperator,
        languageIndicatorStrings,
    }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const { showAction, handlers } = useHoverAction(containerRef);

        const addIndicatorString = () => {
            const newIndicatorString = {
                id: uuidv4(),
                value: "",
            };
            update("languageIndicatorStrings", [
                ...languageIndicatorStrings,
                newIndicatorString,
            ]);
        };

        const updateLanguageIndicatorString = (value: string, id: string) => {
            const updatedStrings = languageIndicatorStrings.map((string) => {
                if (string.id === id) {
                    return { ...string, value };
                }
                return string;
            });
            update("languageIndicatorStrings", updatedStrings);
        };

        const removeLanguageIndicatorString = (id: string) => {
            const updatedStrings = languageIndicatorStrings.filter(
                (string) => string.id !== id,
            );
            update("languageIndicatorStrings", updatedStrings);
        };

        return (
            <Box
                ref={containerRef}
                {...handlers}
                sx={{
                    background: theme.palette.white.main,
                    padding: 1,
                    borderRadius: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <AcxSelect
                        customStyle={selectCustomStyle({ flex: 3 })}
                        fullWidth
                        id={`language-defintiion-localId-${id}`}
                        options={localIdOptions}
                        defaultValue={localIdOptions?.find(
                            (e) => e.value === localeId,
                        )}
                        onChange={(e) => update("localId", e.value)}
                    />
                    <AcxMainTextField
                        fullWidth
                        id={`language-defintiion-field-${id}`}
                        value={fieldPath}
                        placeholderText="Enter Field Path..."
                        onChange={(e) => update("fieldPath", e.target.value)}
                        containerStyle={{ flex: 6 }}
                    />
                    <AcxSelect
                        customStyle={selectCustomStyle({ flex: 3 })}
                        fullWidth
                        id={`language-defintiion-operator-${id}`}
                        options={compareEqConOptions}
                        placeholder="Comparison Operator"
                        defaultValue={compareEqConOptions.find(
                            (e) => e.value === comparisonOperator,
                        )}
                        onChange={(e) => update("comparisonOperator", e.value)}
                    />
                    <IconButton
                        size="small"
                        onClick={addIndicatorString}
                        sx={{
                            padding: theme.spacing(0.5),
                            height: "fit-content",
                            marginLeft: -1,
                        }}
                    >
                        <AddIcon fontSize="inherit" />
                    </IconButton>
                    <DeleteButton
                        onDelete={remove}
                        showDelete={showAction}
                        sx={{
                            marginLeft: -1,
                        }}
                    />
                </Box>
                {!!languageIndicatorStrings.length && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            marginTop: 1,
                        }}
                    >
                        {languageIndicatorStrings.map(({ id, value }) => (
                            <Box
                                key={id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <AcxMainTextField
                                    fullWidth
                                    id={`language-defintiion-source-${id}`}
                                    value={value}
                                    placeholderText="Language Indicator..."
                                    onChange={(e) =>
                                        updateLanguageIndicatorString(
                                            e.target.value,
                                            id,
                                        )
                                    }
                                    containerStyle={{ flex: 30 }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        removeLanguageIndicatorString(id)
                                    }
                                    sx={{
                                        padding: theme.spacing(0.5),
                                        height: "fit-content",
                                        marginRight: 3.75,
                                    }}
                                >
                                    <MinusIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    };

export default LanguageIndicatorDefinition;
